import {SVG, useGLTranslation, useMouseover} from 'notability-services-common-frontend'
import {defineStylesWithBreaks, useThemedStylesWithBreaks} from 'notability-services-common-frontend'
import {ActionButton} from 'notability-services-common-frontend'
import {SquareImage} from 'notability-services-common-frontend'
import {centerVertical} from 'notability-services-common-frontend/src/shared'
import {getGalleryNotePreviewImageURLs} from 'notability-services-common-universal'
import {useAnalytics} from 'notability-services-next-frontend'
import {useMemo, useState} from 'react'

import {addingBasePath, config} from '../config'
import {GridNote} from '../pages'
import {getHyperlinkPropsForModifiedClickSupport} from '../util/eventUtils'
import {generateProfileHref} from '../util/routerUtils'
import {useCustomRouter} from '../util/useCustomRouter'
import {RemixModal} from './modals'

interface RemixNoteViewProps {
  remixes: GridNote[]
  thisNoteId: string
  parentNote?: GridNote
  rootNote?: GridNote
  showsSocialFeatures: boolean
}

const styles = defineStylesWithBreaks((theme, font) => ({
  inspiredSection: {
    height: '56px',
    display: 'flex',
    backgroundColor: theme.core.b2,
    borderRadius: '8px',
    cursor: 'pointer',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },

  remixesLabel: {
    ...font.bodyBold,
    color: theme.core.f1,
    display: 'flex',
    padding: '20px 0px',
    flexDirection: 'row',
    alignSelf: 'center'
  },

  inspiredThumbnail: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'center',
    width: '40px',
    height: '40px',
    margin: '0px 9px'
  },

  inspiredMetadata: {
    ...centerVertical,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'left',
    height: 'fit-content',
    overflow: 'hidden',
    color: theme.core.f1
  },

  title: {
    ...font.caption,
    color: theme.core.f1
  },

  inspiredNoteTitle: {
    ...font.captionBold,
    color: theme.core.f1,
    position: 'relative'
  },

  inspiredAuthor: {
    ...font.caption,
    position: 'relative'
  },

  previewImageContainer: {
    position: 'relative',
    borderRadius: '6px',
    overflow: 'hidden',
    isolation: 'isolate',
    width: '100%'
  },

  remixButton: {
    ...font.captionBold,
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'center',
    width: 'fit-content',
    margin: '16px 14px',
    padding: '6px 20px',
    height: '35px',
    minWidth: '80px'
  },

  previewImageStyle: {
    height: '100%',
    width: 'auto'
  },

  remixIconColor: {
    color: theme.core.f1
  },

  remixIconContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center'
  },

  remixIconPosition: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'center',
    margin: '0px 8px 0px 20px'
  },

  textHover: {
    textDecoration: 'underline'
  }
}))

export default function RemixNoteView(props: RemixNoteViewProps) {
  const {remixes, thisNoteId, parentNote, rootNote, showsSocialFeatures} = props
  const s = useThemedStylesWithBreaks(styles)
  const router = useCustomRouter()
  const {t} = useGLTranslation()
  const {logEvent} = useAnalytics()

  const [isInspiredModalShown, setInspiredModalShown] = useState(false)
  const [isNoteTitleMouseOver, noteTitleMouseListeners] = useMouseover()
  const [isAuthorMouseOver, authorMouseListeners] = useMouseover()

  const profileOnClickAndHref = getHyperlinkPropsForModifiedClickSupport({
    onMetaOrCtrlClick: () => {
      router.pushProfile({screenname: parentNote?.author ?? ''})
    },
    hrefForNormalClick: generateProfileHref(parentNote?.author ?? '', 'shared'),
    onEveryClick: () => {
      void logEvent({
        name: 'Click Remix',
        'Click Target': 'Parent Author'
      })
    }
  })
  const noteOnClickAndHref = getHyperlinkPropsForModifiedClickSupport({
    onMetaOrCtrlClick: () => {
      router.pushNote(parentNote?.id ?? '', parentNote?.name ?? '')
    },
    hrefForNormalClick: addingBasePath(`/${parentNote?.id ?? ''}`),
    onEveryClick: () => {
      void logEvent({
        name: 'Click Remix',
        'Click Target': 'Parent Note'
      })
    }
  })

  const memoizedAuthorStyle = useMemo(() => {
    return {
      ...s.inspiredAuthor,
      ...(isAuthorMouseOver && s.textHover)
    } as React.CSSProperties
  }, [isAuthorMouseOver, s.inspiredAuthor, s.textHover])
  const memoizedNoteTitleStyle = useMemo(() => {
    return {
      ...s.inspiredNoteTitle,
      ...(isNoteTitleMouseOver && s.textHover)
    } as React.CSSProperties
  }, [isNoteTitleMouseOver, s.inspiredNoteTitle, s.textHover])

  const inspiredModal = (
    <RemixModal
      onClose={() => setInspiredModalShown(false)}
      isShown={isInspiredModalShown}
      thisNoteId={thisNoteId}
      rootNote={rootNote}
      parentNote={parentNote}
      remixes={remixes}
    />
  )

  const parentPreviewImages = parentNote ? getGalleryNotePreviewImageURLs(config.API_SERVICE_URL, parentNote.id, 1) : []
  const firstParentPreviewImage = parentPreviewImages[0]

  // Display parent note preview if available, otherwise display note remix count
  const remixContentView =
    parentNote && firstParentPreviewImage ? (
      <div style={s.inspiredSection} onClick={(e) => e.stopPropagation()}>
        <div style={s.inspiredThumbnail} {...noteOnClickAndHref}>
          <div style={s.previewImageContainer}>
            {firstParentPreviewImage && <SquareImage src={firstParentPreviewImage} style={s.previewImageStyle} />}
          </div>
        </div>
        <div style={s.inspiredMetadata}>
          <span style={s.title}>
            {t('remixOf')}{' '}
            <a style={memoizedNoteTitleStyle} {...noteTitleMouseListeners} {...noteOnClickAndHref}>
              {parentNote.name}
            </a>
          </span>
          <span style={s.title}>
            {t('authorAttribution')}{' '}
            {showsSocialFeatures ? (
              <a style={memoizedAuthorStyle} {...authorMouseListeners} {...profileOnClickAndHref}>
                {parentNote.author}
              </a>
            ) : (
              parentNote.author
            )}
          </span>
        </div>
      </div>
    ) : (
      <div style={s.remixIconContainer}>
        <SVG.RemixMedium {...s.remixIconColor} style={s.remixIconPosition} />
        <div style={s.remixesLabel}>
          <span>{t(remixes.length === 1 ? 'remixCount' : 'remixesCount', {count: remixes.length})}</span>
        </div>
      </div>
    )

  if (!showsSocialFeatures) {
    return parentNote && firstParentPreviewImage ? <div style={s.inspiredSection}>{remixContentView}</div> : null
  }

  const buttonTextKey = parentNote ? 'viewRemixes' : 'seeMore'
  const inspiredSection = ((parentNote && firstParentPreviewImage) || remixes.length > 0) && (
    <div
      style={s.inspiredSection}
      onClick={() => {
        void logEvent({
          name: 'View Remixes'
        })
        setInspiredModalShown(true)
      }}
    >
      {remixContentView}
      {remixes.length > 0 && (
        <ActionButton
          appearance="filled"
          size="medium"
          style={s.remixButton}
          text={t(buttonTextKey)}
          onClick={() => setInspiredModalShown(true)}
        />
      )}
    </div>
  )
  return (
    <>
      {inspiredSection}
      {inspiredModal}
    </>
  )
}
